import { useMemo, useState } from "react";

import { Page, Tabs } from "@alpacahq/ui/old";

import { useGetPositions } from "~/v2/api/hooks";
import { POSITIONS_POLLING_INTERVAL } from "~/v2/constants";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { PositionsTable } from "./PositionsTable";

// Kept for its own sake rather than for a caller: the allocation and exposure
// breakdowns that used it were dead and have been removed, but this is a tested
// pure helper and the parsing it guards is easy to get wrong.
export const safeCostBasis = (position: { cost_basis: string }) => {
  const costBasis = Number(position.cost_basis);
  return Number.isFinite(costBasis) ? costBasis : 0;
};

export const Positions = () => {
  const { currentAccount, product } = useAccountContext();

  const { positions } = useGetPositions({
    accountID: currentAccount?.id,
    product,
    options: {
      enabled: !!currentAccount?.id,
      refetchInterval: POSITIONS_POLLING_INTERVAL,
    },
  });

  const longPositions = useMemo(
    () => positions.filter((p) => p.side === "long"),
    [positions],
  );
  const shortPositions = useMemo(
    () => positions.filter((p) => p.side === "short"),
    [positions],
  );
  const optionPositions = useMemo(
    () => positions.filter((p) => p.asset_class === "us_option"),
    [positions],
  );

  const [searchValue, setSearchValue] = useState("");

  return (
    <Page
      title="Positions"
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
      subtitle={
        <>
          View and manage your{" "}
          <span className="font-medium">{positions.length}</span> positions.
        </>
      }
    >
      <Tabs key={currentAccount?.id}>
        <Tabs.Panel name="All">
          <PositionsTable
            persistenceKey="page:positions:v2"
            positions={positions}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
          />
        </Tabs.Panel>
        <Tabs.Panel name="Long">
          <PositionsTable
            persistenceKey="page:positions:long:v2"
            positions={longPositions}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
          />
        </Tabs.Panel>
        <Tabs.Panel name="Short">
          <PositionsTable
            persistenceKey="page:positions:short:v2"
            positions={shortPositions}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
          />
        </Tabs.Panel>
        {currentAccount?.options_approved_level && (
          <Tabs.Panel name="Options">
            <PositionsTable
              persistenceKey="page:positions:options:v2"
              positions={optionPositions}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Page>
  );
};

export default Positions;
